/* eslint-disable no-param-reassign */
import cp from 'class-propper';
import _ from 'lodash';
import uuid from 'uuid/v4';
import dayJS from 'dayjs';
import utc from 'dayjs-plugin-utc';

dayJS.extend(utc);

function q(str, quote = '"') {
  if (!_.isString(str)) str = '';
  return quote + _.trim(str, ` ${quote}`) + quote;
}

const Q_RE = /'([^']+)'/;
const Q_RE_2 = /"([^"]+)"/;
const T_RE = /\t(\d+)\t/;
const STRUCTURE = 'SELECT,FROM,MATCH,WHERE,GROUP BY,ORDER BY,LIMIT,OFFSET,;'.split(',');

function toNumber(value, alternate) {
  if (!(value && _.isString(value) && /[\d]+/.test(value))) return alternate;
  const result = parseInt(value, 10);
  if (Number.isNaN(result) || !_.isNumber(result)) return alternate;
  return result;
}

function reString(value, strings) {
  while (T_RE.test(value)) {
    const m = T_RE.exec(value);
    const index = parseInt(m[1], 10);
    value = value.replace(`\t${index}\t`, `"${strings[index]}"`);
  }
  return value;
}

function termToExpression(ss, value, comp = null) {
  const term = ss.toString();
  if (!comp) {
    comp = '=';
    // assuming value is also not set
    switch (ss.type) {
      case 'varchar':
        value = '"search string"';
        break;
      case 'datetime':
        comp = '<';
        value = `"${dayJS().utc().startOf('day').toISOString()}"`;
        break;
      case 'binary':
        value = 1;
        break;

      case 'text':
        comp = '~';
        break;
      case 'integer':
        value = 100;
        break;

      case 'long':
        value = 1000;
        break;

      case 'float':
        value = '10.01';
        break;

      case 'boolean':
        value = 'true';
        break;

      case 'object':
        return `${ss.toString()}.objectField = 1`;
        break;

      default:
        value = 1;
    }
  } else {
    switch (ss.type) {
      case 'varchar':
        value = q(value || 'string');
        break;

      case 'datetime':
        if (_.isDate(value)) value = value.toISOString();
        value = q(value || new Date().toISOString());
        break;

      case 'binary':
        if ([0, 1].includes(value)) value = 1;
        break;

      case 'text':
        comp = '~';
        value = q(value || 'match');
        break;

      case 'integer':
        if (_.isString(value)) {
          value = parseInt(value, 10);
        }
        if (!_.isNumber(value)) {
          value = 100;
        }
        value = Math.round(value);
        break;

      case 'long':
        if (_.isString(value)) {
          value = parseInt(value, 10);
        }
        if (!_.isNumber(value)) value = 100;
        value = Math.round(value);
        break;

      case 'float':
        if (_.isString(value)) {
          value = parseFloat(value);
        }
        if (!_.isNumber(value)) value = 100;
        break;

      case 'boolean':
        value = value ? 'true' : 'false';
        break;

      case 'object':
        return `${term}.objectField = 1`;
        break;

      default:
        value = 1;
    }
  }
  return `${term} ${comp} ${value}`;
}

class QueryModel {
  constructor(query, name) {
    this.query = query;
    this.name = name;
    this.id = uuid();
  }

  equalTo(otherModel) {
    if (this.select !== otherModel.select) return false;
    if (this.where !== otherModel.where) return false;
    if (this.limit !== otherModel.limit) return false;
    if (this.order !== otherModel.order) return false;
    // intentionally not comparing offset
    return true;
  }

  addOrderField(orderField, desc = false) {
    const term = desc ? `${orderField} DESC` : orderField;
    const orderFields = _((this.order || '').split(','))
      .map(_.trim)
      .reject(a => new RegExp(`${orderField}( DESC)?`).test(a))
      .push(term)
      .compact()
      .uniq()
      .value()
      .join(', ');
    this.order = orderFields;
  }

  addSelectField(item) {
    const selectField = item.toString();
    const selectFieldRoot = selectField.replace(/ (ASC|DESC).*/, '');

    const re = new RegExp(`^${selectFieldRoot}( (ASC|DESC))?$`, 'i');
    const re2 = /\*/;
    const selectFields = _(this.select.split(','))
      .map(_.trim)
      .uniq()
      .compact()
      .reject(f => re.test(f))
      .reject(f => re2.test(f))
      .push(selectField)
      .value();

    if (selectFields.length < 1) {
      this.select = '*, hits()';
    } else {
      if (!selectFields.includes('hits()')) {
        selectFields.push('hits()');
      }
      if (!selectFields.includes('mediaId')) {
        selectFields.push('mediaId'); // needed for playing media
      }
      this.select = selectFields.join(', ');
    }
  }

  addWhereField(name) {
    if (this.where) {
      this.where += ` AND ${name}`;
    } else {
      this.where = name;
    }
  }

  addField(ss, target, value, comp, desc) {
    console.log('QueryModel-----addField:', ss, 'target:', target, 'value:', value, 'comp:', comp, 'desc:', desc);

    switch (target) {
      case 'select':
        this.addSelectField(ss);
        break;

      case 'where':
        this.addWhereField(termToExpression(ss, value, comp));
        break;

      case 'order':
        this.addOrderField(ss, desc);
        break;
    }
  }

  composed(page = false) {
    let out = `SELECT ${this.select || '*, hits()'} FROM ${this.from || 'media'} `;
    STRUCTURE.slice(2).forEach((term) => {
      if (term === ';') {
        return;
      }
      const termLC = term.toLowerCase().replace(' by', '');
      const value = this[termLC];
      const list = []; // only for "WHERE" -- eslint yells about case declarations
      switch (term) {
        case 'LIMIT':
          if (value !== '10' && value !== 10) {
            out += ` ${term} ${value} `;
          }
          break;

        case 'OFFSET':
          if (page !== false) {
            if (page) {
              const clause = ` ${term} ${(this.limit || 10) * page}`;
              out += clause;
            } // else omit term entirely
          } else if (value) {
            out += ` ${term} ${value} `;
          }
          break;

        case 'WHERE':
          if (this.useBeforeDate && this.beforeDate) {
            list.push(`dateCreated < "${this.beforeDateString()}"`);
          }
          if (this.useAfterDate && this.afterDate) {
            list.push(`dateCreated >= "${this.afterDateString()}"`);
          }
          if (_.trim(value)) {
            list.push(value);
          }
          if (list.length) {
            out += `${term} ${list.join(' AND ')}`;
          }
          break;

        default:
          if (_.trim(value)) {
            out += ` ${term} ${value} `;
          }
      }
    });

    return out.replace(/\s+/g, ' ');
  }

  compose() {
    try {
      this._query = this.composed();
    } catch (err) {
      console.log('error composing: ', err, this);
    }
  }

  _midnight(date) {
    return dayJS(date).startOf('day').toISOString();
  }

  beforeDateString() {
    if (!this.beforeDate) {
      return new Date().toISOString();
    }
    if (_.isDate(this.beforeDate)) {
      return this._midnight(this.beforeDate);
    }
    if (_.isString(this.beforeDate)) {
      return this.beforeDate;
    }
    return '';
  }

  afterDateString() {
    if (!this.afterDate) {
      return new Date().toISOString();
    }
    if (_.isDate(this.afterDate)) {
      return this._midnight(this.afterDate);
    }
    if (_.isString(this.afterDate)) {
      return this.afterDate;
    }
    return '';
  }

  get query() {
    return this._query;
  }

  nextPage() {
    this.page = this.page + 1;
  }

  prevPage() {
    this.page = Math.max(this.page - 1, 0);
  }

  set totalResults(n) {
    if (this._totalResults !== n) {
      this._totalResults = n;
      this.page = 0;
    }
  }

  get totalResults() {
    return this._totalResults || 0;
  }

  set query(value) {
    if (value && _.isString(value)) {
      this._query = value;
    } else {
      this._query = '';
    }
    try {
      this.digest();
    } catch (err) {
      console.log('set query digest error: ', err);
    }
  }

  get limit() {
    if (!('_limit' in this)) {
      return 10;
    }
    return this._limit;
  }

  set limit(value) {
    if (_.isNumber(value) && value >= 1) {
      this._limit = value;
    }
  }

  digest(query) {
    query = _.trim(`${this.query}`).replace(/[\n\r\s]/g, ' ');
    console.log('digesting ', query);
    if (!query) {
      // reset defaults?
      return;
    }
    if (!_.endsWith(query, ';')) {
      query += ';';
    }

    const quotedStrings = [];

    [Q_RE, Q_RE_2].forEach((RE) => {
      let m;
      do {
        m = RE.exec(query);
        if (m) {
          const term = m[1];
          quotedStrings.push(term);
          query = query.replace(m[0], `\t${(quotedStrings.length - 1)}\t`);
        }
      } while (m);
    });

    STRUCTURE.forEach((term, index) => {
      if (term === ';') {
        return;
      }
      let i = index + 1;
      let found = false;
      while ((!found) && i < STRUCTURE.length) {
        const RE = new RegExp(`${term}(.*)${STRUCTURE[i]}`, 'i');
        const wm = RE.exec(query);
        if (wm) {
          let value = reString(wm[1], quotedStrings);
          const name = term.toLowerCase().replace(' by', '');
          found = true;
          // @TODO: extract beforeDate, afterDate
          switch (name) {
            case 'limit':
              value = toNumber(value, 0);
              break;

            case 'offset':
              value = toNumber(value, 0);
              break;
            default:
          }
          this[name] = value;
        }
        i += 1;
      }
    });
  }

  toJSON() {
    return {
      querySelect: this.select || '*, hits()',
      queryWhere: this.where || '',
      queryFrom: this.from || 'media',
      queryOrderBy: this.order || '',
      limit: this.limit || 10,
      offset: this.offset || 0,
    };
  }

  clone() {
    const out = new QueryModel(this.query, this.name);
    const id = out.id;
    Object.assign(out, this, { id, __from: this.id });
    console.log('clone result: ', out);
    return out;
  }
}

cp(QueryModel)
  .addProp('beforeDate', {
    defaultValue: false,
  })
  .addProp('afterDate', {
    defaultValue: false,
  })
  .addProp('useBeforeDate', {
    type: 'boolean',
    defaultValue: false,
  })
  .addProp('useAfterDate', {
    type: 'boolean',
    defaultValue: false,
  })

  .addProp('offset', {
    type: 'number',
    defaultValue: 0
    ,
  })
  .addProp('select', {
    type: 'string',
    defaultValue: '',
  })
  .addProp('from', {
    type: 'string',
    defaultValue: 'media',
  })
  .addProp('where',{
    type: 'string',
    defaultValue: '',
  })
  .addProp('match', {
    type: 'string',
    defaultValue: '',
  })
  .addProp('group', {
    type: 'string',
    defaultValue: '',
  })
  .addProp('order', {
    type: 'string',
    defaultValue: '',
  });

export default QueryModel;
